import type { OrbitControlsProps } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import React from 'react'
import { useSpring } from 'react-spring'
import * as THREE from 'three'

interface SpringAnimateProps {
  initTarget?: number[]
  initPosition?: number[]
  orbitRef?: OrbitControlsProps | null
  mass?: number
  tension?: number
  friction?: number
  customTargetInterpolation?: (arg: THREE.Vector3) => THREE.Vector3
}

const SpringAnimate = ({
  initTarget,
  initPosition,
  orbitRef,
  mass = 1,
  tension = 280,
  friction = 40,
  customTargetInterpolation,
}: SpringAnimateProps) => {
  const { camera } = useThree()

  const [target, setTarget] = React.useState(initTarget)
  const [position, setPosition] = React.useState(initPosition)

  const isFirstRender = React.useRef(true)

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
    }
  }, [target, position])

  const [springs, api] = useSpring(
    () => ({
      config: {
        mass,
        tension,
        friction,
      },
      from: {
        target: initTarget || [],
        position: initPosition || [],
      },
    }),
    []
  )

  React.useEffect(() => {
    api.start({
      to: {
        target,
        position,
      },
      onChange: ({ value }) => {
        if (orbitRef) {
          const orbitControl = orbitRef as OrbitControlsProps
          if (customTargetInterpolation) {
            orbitControl.target = customTargetInterpolation(
              new THREE.Vector3(...value.position)
            )
          } else {
            orbitControl.target = new THREE.Vector3(...value.target)
          }
        }
        camera.position.fromArray(value.position)
      },
    })
  }, [target, position])

  return {
    target,
    position,
    springs,
    setTarget,
    setPosition,
  }
}

export default SpringAnimate
